import type { ITooltipContentProps } from '@opengov/components-visualization/dist/Chart/TooltipContent/TooltipContent';
import { composeStateMachine } from '@opengov/compose-app/core/module';
import { ICategorizedPoint, ICoordinate, IGraphElement, IRectangle } from '@opengov/data-viz';
import { IOgqlQueryResult, IOgqlV1QueryResult } from '@opengov/ogql-types';
import { createReportConfiguration, IDataValuesFormat, IReportConfiguration } from '@opengov/reporting-types';
import { castDraft, produce } from 'immer';
export interface ITooltipBase {
  boundingClientRect: IRectangle;
  coordinate: ICoordinate;
  domCoordinate: ICoordinate;
  data: ITooltipContentProps;
  targetSvgElement: SVGGeometryElement;
  show: 'show' | 'measure' | 'hide';
  targetElementRect: IRectangle;
}

export interface ITooltip extends ITooltipBase {
  reportBoundingClientRect?(rect: IRectangle, show: 'show' | 'measure' | 'hide'): void;
  onMouseLeave?(e): void;
}

export interface IVisualizationResult {
  data: IOgqlQueryResult['data'];
  columns: IOgqlQueryResult['columns'] | IOgqlV1QueryResult['metadata']['columns'];
}

export type IChartState = {
  selectedCategory: string;
  selectedElements: IGraphElement[];
  selectedPoint: ICategorizedPoint;
  chartContainerRect: IRectangle;
  containerWidth: number;
  chartData: IDataValuesFormat[];
  reportConfiguration: IReportConfiguration;
  tooltip: ITooltipBase;
  visualizationResult: IVisualizationResult;
};

export const ChartState: IChartState = {
  selectedCategory: null,
  selectedElements: [],
  selectedPoint: null,
  containerWidth: 1200,
  chartData: [],
  reportConfiguration: createReportConfiguration(),
  chartContainerRect: { x: -1, y: -1, bottom: 0, top: 0, left: 0, right: 0, width: 0, height: 0 },
  tooltip: {
    data: null,
    boundingClientRect: { x: -1, y: -1, bottom: 0, top: 0, left: 0, right: 0, width: 0, height: 0 },
    targetElementRect: { x: -1, y: -1, bottom: 0, top: 0, left: 0, right: 0, width: 0, height: 0 },
    domCoordinate: { x: 0, y: 0 },
    show: 'hide',
    coordinate: null,
    targetSvgElement: null
  },
  visualizationResult: {
    data: [],
    columns: []
  }
};

export const chartInitialState = ChartState;

export const chartState = composeStateMachine('chart', chartInitialState, (compose) => ({
  setContainerWidth: compose.action((state, payload: number) =>
    produce(state, (newState) => {
      newState.containerWidth = payload;
    })
  ),
  setChartContainerRect: compose.action((state, payload: IRectangle) =>
    produce(state, (newState) => {
      newState.chartContainerRect = payload;
    })
  ),
  setChartDataAndReportConfiguration: compose.action(
    (state, payload: { chartData: any[]; reportConfiguration: IReportConfiguration }) =>
      produce(state, (newState) => {
        newState.chartData = payload.chartData;
        newState.reportConfiguration = castDraft(payload.reportConfiguration);
      })
  ),
  setSelectedCategory: compose.action((state, payload: string) =>
    produce(state, (newState) => {
      newState.selectedCategory = payload;
    })
  ),
  setSelectedPoint: compose.action((state, payload: ICategorizedPoint) =>
    produce(state, (newState) => {
      newState.selectedPoint = payload;
    })
  ),
  setSelectedElements: compose.action((state, payload: IGraphElement[]) =>
    produce(state, (newState) => {
      newState.selectedElements = payload;
    })
  ),
  setTooltip: compose.action((state, payload: ITooltip) =>
    produce(state, (newState) => {
      newState.tooltip = payload as any;
    })
  ),
  setVisualizationResultAndReportConfiguration: compose.action(
    (state, payload: { visualizationResult: IVisualizationResult; reportConfiguration: IReportConfiguration }) =>
      produce(state, (newState) => {
        newState.visualizationResult = payload.visualizationResult;
        newState.reportConfiguration = castDraft(payload.reportConfiguration);
      })
  )
}));

export const {
  actions: chartActions,
  actionCreators: chartActionCreators,
  reducer: chartReducer,
  selector: chartSelector
} = chartState;
